import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  PressConferenceCalendarEntry,
  PressConferenceCalendarFilters,
  PressConferenceItem,
  PressConferenceRequest,
} from './pressConferenceTypes';

const BASE = '/api/press-conferences';

/**
 * Serializes a list query. A facet is written whenever its key is PRESENT, empty string included —
 * an empty value is the reader saying "none of these", which matches no row.
 */
function serialize(query: ListQuery & { conferenceStatus?: string }): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);
  if (query.conferenceStatus !== undefined) params.set('conferenceStatus', query.conferenceStatus);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

export const pressConferencesApi = {
  list: (query: ListQuery & { conferenceStatus?: string }, signal?: AbortSignal) =>
    api<PagedResult<PressConferenceItem>>(`${BASE}${serialize(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<PressConferenceItem>(`${BASE}/${id}`, { signal }),

  create: (body: PressConferenceRequest) => api<PressConferenceItem>(BASE, { method: 'POST', body }),

  update: (id: string, body: PressConferenceRequest) => api<PressConferenceItem>(`${BASE}/${id}`, { method: 'PUT', body }),

  markHeld: (id: string) => api<PressConferenceItem>(`${BASE}/${id}/hold`, { method: 'POST' }),

  cancel: (id: string, reason: string) =>
    api<PressConferenceItem>(`${BASE}/${id}/cancel`, { method: 'POST', body: { reason } }),

  /** Back to Scheduled — unlocks a held or cancelled conference. Its own key, `reopen`. */
  reopen: (id: string) => api<PressConferenceItem>(`${BASE}/${id}/reopen`, { method: 'POST' }),

  remove: (id: string) => api<void>(`${BASE}/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`${BASE}/${id}/restore`, { method: 'POST' }),

  /**
   * Every press conference between two calendar dates, inclusive — at most a year. Takes the
   * register's filters, written on PRESENCE as the list's are; none at all is every live row.
   */
  calendar: (from: string, to: string, filters: PressConferenceCalendarFilters = {}, signal?: AbortSignal) => {
    const params = new URLSearchParams({ from, to });

    if (filters.search?.trim()) params.set('search', filters.search.trim());
    if (filters.status !== undefined) params.set('status', filters.status);
    if (filters.conferenceStatus !== undefined) params.set('conferenceStatus', filters.conferenceStatus);

    return api<PressConferenceCalendarEntry[]>(`${BASE}/calendar?${params.toString()}`, { signal });
  },
};
